import React, { useContext, useState } from 'react';
import { Redirect, RouteComponentProps, withRouter } from 'react-router';
import UserContext from '../../contexts/UserContext';
import {
  AdminNotice,
  EmptyState,
  PageHeader,
} from '../../components/admin/Shared';
import PricingTab from '../Admin/Partners/PricingTab';
import VehiclesTab from '../Admin/Partners/VehiclesTab';
import ServiceAreasTab from '../Admin/Partners/ServiceAreasTab';
import {
  getCanControlLogistics,
  getPartnerIdFromUser,
  getRole,
} from '../../services/storage';
import { Role } from '../../utilities/Role';

// Note: AccessTab (canControlLogistics promotion) is admin-only — partner
// users should not promote each other.

type Section = 'pricing' | 'vehicles' | 'service-areas';

/**
 * Partner portal — self-configuration screens.
 *
 * Reuses the admin Partners tab components (PricingTab / VehiclesTab /
 * ServiceAreasTab) and constrains them to the user's own `partnerId`
 * (read from the JWT, never the URL). Role-gated per the matrix in the
 * fan-out prompt:
 *   Master           → Pricing (both scopes) + Vehicles + Service Areas, full edit.
 *   Driver / Courier → Pricing (courier, read-only) + Vehicles + Service Areas, read-only.
 *   Warehouse staff  → Pricing (warehouse, read-only). No vehicles, no service areas.
 *
 * Backend (Track A) loosened the admin endpoints to accept partner principals
 * constrained to their own partnerId; cross-partner access returns 403.
 */
function PartnerConfigurationPage(_props: RouteComponentProps) {
  const { user } = useContext(UserContext);
  const [section, setSection] = useState<Section>('pricing');

  // 401 — not signed in.
  if (!user) {
    return <Redirect to="/login" />;
  }

  const role = getRole(user);
  const partnerId = getPartnerIdFromUser(user);
  const canControlLogistics = getCanControlLogistics(user);

  const isAdminOrWarehouseAdmin = role === Role.Admin || role === Role.Warehouse;
  const isMaster = role === 'PARTNER_MASTER' || role === 'MASTER';
  const isDriver = role === 'PARTNER_DRIVER' || role === 'DRIVER' || role === 'COURIER';
  const isWarehouseStaff = role === 'PARTNER_WAREHOUSE';

  // Admin / internal warehouse roles use the admin portal — bounce them.
  if (isAdminOrWarehouseAdmin) {
    return (
      <>
        <PageHeader title="Configuration" />
        <EmptyState
          variant="error-fallback"
          title="Use the admin portal"
          body="Admin and warehouse roles configure partners via the admin portal at /admin/partners."
        />
      </>
    );
  }

  // 403 — partner principal but missing partnerId, or unrecognised role.
  if (partnerId == null || (!isMaster && !isDriver && !isWarehouseStaff)) {
    return (
      <>
        <PageHeader title="Configuration" />
        <EmptyState
          variant="error-fallback"
          title="Access denied"
          body="This page is for partner users. If you should have access, contact Kit Keeper support."
        />
      </>
    );
  }

  // Visible tab list per role.
  const tabs: { key: Section; label: string }[] = [];
  if (isMaster || isDriver) tabs.push({ key: 'pricing', label: 'Pricing' });
  if (isWarehouseStaff) tabs.push({ key: 'pricing', label: 'Pricing' });
  if (isMaster || isDriver) tabs.push({ key: 'vehicles', label: 'Vehicles' });
  if (isMaster || isDriver) tabs.push({ key: 'service-areas', label: 'Service areas' });

  // If the current section isn't visible to this role, fall back to the first tab.
  const visibleSection: Section = tabs.some((t) => t.key === section)
    ? section
    : tabs[0]?.key ?? 'pricing';

  const renderSection = () => {
    if (visibleSection === 'pricing') {
      // Driver: courier rates only, read-only.
      // Warehouse: warehouse rates only, read-only.
      // Master: both scopes, full edit (subject to canControlLogistics on the
      // backend; the UI does not pre-block writes — we let 403 surface as a
      // friendly notice via errorMessage).
      if (isMaster) {
        // Master always sees both scopes; the backend gates writes by
        // canControlLogistics and will return 403 if the capability is off.
        // We surface a notice above when the capability is missing so the
        // master is not surprised by a 403 on save.
        return (
          <PricingTab partnerId={partnerId} pricingScope="all" />
        );
      }
      if (isDriver) {
        return (
          <PricingTab partnerId={partnerId} pricingScope="courier" readOnly />
        );
      }
      // Warehouse staff.
      return (
        <PricingTab partnerId={partnerId} pricingScope="warehouse" readOnly />
      );
    }
    if (visibleSection === 'vehicles') {
      return <VehiclesTab partnerId={partnerId} readOnly={!isMaster} />;
    }
    return <ServiceAreasTab partnerId={partnerId} readOnly={!isMaster} />;
  };

  // Friendly notice when a master lacks canControlLogistics — the backend
  // still gates writes; we surface why up front so they understand any 403
  // they hit when trying to save.
  const showCapabilityNotice = isMaster && !canControlLogistics;

  return (
    <>
      <PageHeader title="Configuration" />

      {showCapabilityNotice && (
        <AdminNotice intent="info">
          You can view this partner's configuration. Saving changes requires the
          logistics-control capability — ask a Kit Keeper admin to grant it on
          your account.
        </AdminNotice>
      )}

      {tabs.length > 1 && (
        <div className="admin-tab-bar">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              className={
                visibleSection === t.key
                  ? 'admin-tab-bar__tab admin-tab-bar__tab--active'
                  : 'admin-tab-bar__tab'
              }
              onClick={() => setSection(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      <section className="admin-partner-detail">{renderSection()}</section>
    </>
  );
}

export default withRouter(PartnerConfigurationPage);
